import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, ExternalLink, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FAQAnswerContent } from "@/components/faq/FAQAnswerContent";
import { faqQuestions } from "@/data/nis2-faq-data";
import { ScrollArea } from "@/components/ui/scroll-area";

const NIS2FAQAnswer = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const question = faqQuestions.find((q) => q.id === id);
  const relatedQuestions =
    question?.relatedQuestions
      ?.map((relId) => faqQuestions.find((q) => q.id === relId))
      .filter(Boolean) || [];

  if (!question) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-foreground">
            Întrebarea nu a fost găsită
          </h1>
          <Button onClick={() => navigate("/nis2-faq")}>Înapoi la FAQ</Button>
        </div>
      </div>
    );
  }

  // Upcoming deadlines
  const upcomingDeadlines = [
    {
      date: "17 aprilie 2025",
      title: "Implementare măsuri minime",
      urgent: true,
    },
    {
      date: "17 octombrie 2025",
      title: "Conformare completă",
      urgent: true,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/nis2-faq/browse")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi la întrebări
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          {/* Answer Content */}
          <div>
            <FAQAnswerContent
              question={question}
              relatedQuestions={relatedQuestions}
              onRelatedClick={(questionId) =>
                navigate(`/nis2-faq/answer/${questionId}`)
              }
            />
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Quick Links */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold text-foreground">
                  Link-uri utile
                </h3>
                <div className="space-y-3">
                  <a
                    href="https://www.dnsc.ro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Website DNSC
                  </a>
                  <a
                    href="https://cert.ro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    CERT-RO
                  </a>
                  <button
                    onClick={() =>
                      window.open("/assets/oug-155-2024.pdf", "_blank")
                    }
                    className="flex items-center gap-2 text-sm text-primary hover:underline w-full text-left"
                  >
                    <Download className="w-4 h-4" />
                    Descarcă OUG 155/2024
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Deadlines */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold text-foreground">
                  Termene importante
                </h3>
                <div className="space-y-3">
                  {upcomingDeadlines.map((deadline, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                    >
                      <Calendar className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-foreground">
                          {deadline.date}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {deadline.title}
                        </div>
                        {deadline.urgent && (
                          <div className="text-xs font-medium text-destructive">
                            Urgent
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold text-foreground">Contact</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    <strong className="text-foreground">DNSC:</strong>
                    <br />
                    contact@dnsc.ro
                  </p>
                  <p>
                    <strong className="text-foreground">CERT-RO:</strong>
                    <br />
                    incidente@cert.ro
                    <br />
                    +40 21 311 48 42
                  </p>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default NIS2FAQAnswer;
